import { i18nService } from './i18n';
import { normalizeSessionTitle } from '../../shared/sessionMetadata';
import { selectedConversationMessageIds } from '../../shared/conversationTurns';
import { getTargetClient } from './relay';
import { readConversationLink, buildConversationLink } from './conversationLink';
import { notifyCreditRefresh } from './creditRefresh';
import { terminalSessionStatus, turnSummaryMetadata } from '../utils/coworkTurnSummary';
import { store } from '../store';
import { rememberDeliverableArtifact } from './deliverableArtifacts';
import {
  removeSessionMessages,
  upsertCoworkDocumentExtractProgress,
  setCloudSessions,
  setCurrentSession,
  setCurrentSessionId,
  updateCurrentSessionUsage,
  addSession,
  updateSessionStatus,
  deleteSession as deleteSessionAction,
  addMessage,
  updateMessageContent,
  updateMessageMetadata,
  setStreaming,
  updateSessionPinned,
  updateSessionTitle,
  enqueuePendingPermission,
  dequeuePendingPermission,
  setConfig,
  updateSessionExecutionMode,
  clearCurrentSession,
  addRegisteredDeliverables,
  setQueuedMessages,
  clearQueuedMessages,
  updateSessionModelId,
} from '../store/slices/coworkSlice';
import type {
  CoworkSession,
  CoworkConfigUpdate,
  CoworkApiConfig,
  CoworkUserMemoryEntry,
  CoworkMemoryStats,
  CoworkPermissionResult,
  CoworkStartOptions,
  CoworkContinueOptions,
  SandboxProfile,
  SandboxImage,
} from '../types/cowork';

/**
 * 刷新页面后要恢复的会话 id。按用户分键，避免换账号后把别人的会话打开。
 *
 * 老版本 Web 会话 id 不在 URL 里，刷新后 Redux 全清 → 回到"新建任务"首页。
 * 实测（长任务运行中刷新）：任务在后台继续跑、左侧列表也有它，但主区变欢迎页、
 * 输入框的模型还退回默认，用户以为任务没了。这里把最后打开的会话记在 localStorage，
 * 启动时按会话列表校验后恢复。
 */
const LAST_SESSION_KEY_PREFIX = 'boclaw_last_cowork_session';

function lastSessionKey(): string {
  let userId = '';
  try {
    userId = localStorage.getItem('boclaw_user_id') || '';
  } catch { /* 隐私模式/禁用存储 */ }
  return userId ? `${LAST_SESSION_KEY_PREFIX}::${userId}` : LAST_SESSION_KEY_PREFIX;
}

function rememberLastSession(sessionId: string | null): void {
  try {
    if (sessionId) localStorage.setItem(lastSessionKey(), sessionId);
    else localStorage.removeItem(lastSessionKey());
  } catch { /* 存储不可用时静默降级为不记忆 */ }
}

function readLastSession(): string | null {
  try {
    return localStorage.getItem(lastSessionKey());
  } catch {
    return null;
  }
}

class CoworkService {
  private streamListenerCleanups: Array<() => void> = [];
  private usageRefreshTimers = new Map<string, number>();
  private usageRefreshRequestVersions = new Map<string, number>();
  private nextUsageRefreshRequestVersion = 0;
  private initialized = false;
  private sessionMutations = new Map<string, symbol>();
  private sessionListSequence = 0;
  private initializationEpoch = 0;
  private sessionWatchCleanup: (() => void) | null = null;
  private navigationCleanup: (() => void) | null = null;
  // 流式看门狗：stream 事件是 fire-and-forget 推送(见 server sendToUserAndRelays，
  // 无重放缓冲)。若浏览器 WS 出现"不干净"的中断——后台标签页被节流、屏幕共享/远程
  // 桌面、网络微抖动——事件在 readyState!==OPEN 时被静默丢弃，而 onclose→重连补偿
  // 只在"干净关闭"时触发，于是会出现转圈卡死、刷新才恢复。看门狗在会话 running 且
  // 一段时间无任何 stream 活动时，主动 loadSession 对账补齐。
  private lastStreamActivityAt = 0;
  private streamWatchdogTimer: number | null = null;

  async init(): Promise<void> {
    if (this.initialized) return;
    const epoch = ++this.initializationEpoch;

    // Load initial config
    await this.loadConfig();

    // Load sessions list
    await this.loadSessions();

    // 旧账号初始化的迟到结果不能重新挂载事件订阅。
    if (epoch !== this.initializationEpoch) return;
    // Set up stream listeners
    this.setupStreamListeners();

    this.initialized = true;

    // 刷新后恢复上次打开的会话（见 LAST_SESSION_KEY_PREFIX 的说明）。
    // 顺序要紧：先把已存的 id 读出来，再挂订阅——订阅会把"当前无选中会话"这个初始
    // 状态写回去，先挂就会把要恢复的 id 抹掉。
    const pendingRestoreId = readLastSession();
    this.watchCurrentSession();
    const linked = readConversationLink(window.location.href);
    const navigate = async () => {
      const target = readConversationLink(window.location.href);
      window.dispatchEvent(new Event('cowork:show'));
      if (!target) { this.clearSession(); return; }
      try {
        let session: CoworkSession | null;
        if (target.clientId) {
          const { relayService } = await import('./relay');
          await relayService.ensureConnected();
          session = await relayService.loadDesktopSession(target.clientId, target.sessionId);
        } else session = await this.loadSession(target.sessionId);
        if (!session) throw new Error('会话不存在或当前账号无权访问');
      } catch (error) {
        window.dispatchEvent(new CustomEvent('app:showToast', { detail: error instanceof Error ? error.message : '无法打开会话' }));
      }
    };
    window.addEventListener('popstate', navigate);
    this.navigationCleanup = () => window.removeEventListener('popstate', navigate);
    if (linked) await navigate();
    else await this.restoreLastSession(pendingRestoreId);
  }

  /**
   * 订阅 currentSessionId 的变化并持久化。
   *
   * 不在各个调用点分别记：新建会话走的是 CoworkView 里直接 dispatch(setCurrentSession)，
   * 根本不经过 loadSession——第一版只在 loadSession 里记，结果"新建会话后刷新"这个
   * 最常见的场景恰好一个字都没存下来，恢复自然不生效。订阅是唯一能覆盖
   * 新建 / 点选 / 恢复 / 清空 全部路径的单点。
   */
  private watchCurrentSession(): void {
    let prev = store.getState().cowork.currentSessionId;
    this.sessionWatchCleanup?.();
    this.sessionWatchCleanup = store.subscribe(() => {
      const cur = store.getState().cowork.currentSessionId;
      if (cur === prev) return;
      prev = cur;
      rememberLastSession(cur);
      if (cur?.startsWith('temp-')) return;
      const state = store.getState().cowork;
      const session = state.currentSession || state.sessions.find(item => item.id === cur);
      const link = readConversationLink(window.location.href);
      const desktopClient = (session?.origin === 'desktop' && cur ? getTargetClient(cur) : null)
        || (link?.sessionId === cur ? link.clientId : undefined);
      // 桌面路由由成功加载后返回的设备归属填充；无归属时不生成可能指向云端的错误链接。
      if (session?.origin === 'desktop' && !desktopClient) return;
      const target = cur ? { sessionId: cur, ...(desktopClient ? { clientId: desktopClient } : {}) } : null;
      const href = buildConversationLink(window.location.href, target);
      if (href !== window.location.href) window.history.pushState(null, '', href);
    });
  }

  /** 尝试恢复上次打开的会话；失败或无记录时什么都不做。 */
  private async restoreLastSession(lastId: string | null): Promise<void> {
    try {
      const state = store.getState().cowork;
      if (state.currentSessionId) return;
      if (!lastId) return;
      // 新建会话时前端会先用一个本地临时 id 占位，它不会出现在服务端返回的列表里；
      // 这里的存在性校验同时挡掉临时 id、已删除的会话和换账号后的残留。
      if (!state.sessions.some((s) => s.id === lastId)) {
        rememberLastSession(null);
        return;
      }
      await this.loadSession(lastId);
    } catch (err) {
      console.warn('[cowork] restore last session failed:', err);
    }
  }

  private setupStreamListeners(): void {
    const cowork = window.electron?.cowork;
    if (!cowork) return;

    // Clean up any existing listeners
    this.cleanupListeners();

    // Message listener - also check if session exists (for IM-created sessions)
    const messageCleanup = cowork.onStreamMessage(async ({ sessionId, message }) => {
      // Check if session exists in current list
      const state = store.getState().cowork;
      const sessionExists = state.sessions.some(s => s.id === sessionId);

      if (!sessionExists) {
        // Session was created by IM or another source, refresh the session list
        await this.loadSessions();
      }

      // A new user turn means this session is actively running again
      // (especially important for IM-triggered turns that do not call continueSession from renderer).
      if (message.type === 'user') {
        store.dispatch(updateSessionStatus({ sessionId, status: 'running' }));
      }

      // Do not force status back to "running" on arbitrary messages.
      // Late stream chunks can arrive after an error/complete event.
      store.dispatch(addMessage({ sessionId, message }));
      this.markStreamActivity(sessionId);
      this.scheduleCurrentSessionUsageRefresh(sessionId);
    });
    this.streamListenerCleanups.push(messageCleanup);

    // Message update listener (for streaming content updates)
    const messageUpdateCleanup = cowork.onStreamMessageUpdate(({ sessionId, messageId, content, metadata }) => {
      store.dispatch(updateMessageContent({ sessionId, messageId, content, metadata }));
      this.markStreamActivity(sessionId);
      this.scheduleCurrentSessionUsageRefresh(sessionId);
    });
    this.streamListenerCleanups.push(messageUpdateCleanup);

    // DocumentExtract page progress (live tool card only).
    const documentExtractProgressCleanup = cowork.onStreamDocumentExtractProgress?.((event) => {
      store.dispatch(upsertCoworkDocumentExtractProgress(event));
    });
    if (documentExtractProgressCleanup) this.streamListenerCleanups.push(documentExtractProgressCleanup);

    // Permission request listener
    const feedbackMetadataCleanup = cowork.onStreamMessageMetadata?.(({ sessionId, messageId, metadata }) => {
      store.dispatch(updateMessageMetadata({ sessionId, messageId, metadata }));
    });
    if (feedbackMetadataCleanup) this.streamListenerCleanups.push(feedbackMetadataCleanup);

    // Permission request listener
    const permissionCleanup = cowork.onStreamPermission(({ sessionId, request }) => {
      store.dispatch(enqueuePendingPermission({
        sessionId,
        toolName: request.toolName,
        toolInput: request.toolInput,
        requestId: request.requestId,
        toolUseId: request.toolUseId ?? null,
      }));
    });
    this.streamListenerCleanups.push(permissionCleanup);

    // WEB-01: close a modal whose request the server invalidated.
    if (cowork.onStreamPermissionResolved) {
      const permissionResolvedCleanup = cowork.onStreamPermissionResolved(({ requestId }) => {
        if (requestId) store.dispatch(dequeuePendingPermission({ requestId }));
      });
      this.streamListenerCleanups.push(permissionResolvedCleanup);
    }

    // Complete listener
    const completeCleanup = cowork.onStreamComplete(({ sessionId, turnSummary }) => {
      const metadata = turnSummaryMetadata(turnSummary);
      if (metadata && turnSummary) store.dispatch(updateMessageMetadata({ sessionId, messageId: turnSummary.userMessageId, metadata }));
      store.dispatch(updateSessionStatus({ sessionId, status: terminalSessionStatus(turnSummary) }));
      this.markStreamActivity(sessionId);
      this.refreshCurrentSessionUsage(sessionId);
      notifyCreditRefresh();
    });
    this.streamListenerCleanups.push(completeCleanup);

    // Error listener
    const errorCleanup = cowork.onStreamError(({ sessionId, turnSummary }) => {
      const metadata = turnSummaryMetadata(turnSummary);
      if (metadata && turnSummary) store.dispatch(updateMessageMetadata({ sessionId, messageId: turnSummary.userMessageId, metadata }));
      store.dispatch(updateSessionStatus({ sessionId, status: 'error' }));
      this.markStreamActivity(sessionId);
      this.refreshCurrentSessionUsage(sessionId);
      notifyCreditRefresh();
    });
    this.streamListenerCleanups.push(errorCleanup);

    // 排队顺序与发送时机完全由服务端 Runner 管理；浏览器只消费权威快照。
    if (cowork.onMessagesDeleted) {
      this.streamListenerCleanups.push(cowork.onMessagesDeleted(data => {
        if (typeof data.sessionId === 'string' && Array.isArray(data.deletedMessageIds)) store.dispatch(removeSessionMessages(data));
      }));
    }
    const queueUpdateCleanup = cowork.onStreamQueueUpdate(({ sessionId, queued }) => {
      store.dispatch(setQueuedMessages({
        sessionId,
        queued: Array.isArray(queued) ? queued : [],
      }));
    });
    this.streamListenerCleanups.push(queueUpdateCleanup);

    const usageCleanup = cowork.onStreamUsage(({ sessionId, usage }) => {
      store.dispatch(updateCurrentSessionUsage({ sessionId, usage }));
    });
    this.streamListenerCleanups.push(usageCleanup);

    // ExecutionMode change listener
    if (cowork.onStreamExecutionModeChange) {
      const executionModeCleanup = cowork.onStreamExecutionModeChange(({ sessionId, executionMode }) => {
        store.dispatch(updateSessionExecutionMode({ sessionId, executionMode: executionMode as any }));
      });
      this.streamListenerCleanups.push(executionModeCleanup);
    }

    // Deliverable registration stream (sandbox Write/Edit auto-detect + explicit
    // /register-deliverable + turn-end scan).
    if (cowork.onStreamDeliverableRegister) {
      const deliverableCleanup = cowork.onStreamDeliverableRegister(({ sessionId, paths }) => {
        if (sessionId && Array.isArray(paths) && paths.length > 0) {
          store.dispatch(addRegisteredDeliverables({ sessionId, paths }));
        }
      });
      this.streamListenerCleanups.push(deliverableCleanup);
    }

    // WEB-09: Host-owned artifact copies of deliverables (downloadable after pause).
    if (cowork.onStreamDeliverableArtifact) {
      const artifactCleanup = cowork.onStreamDeliverableArtifact((data) => {
        rememberDeliverableArtifact(data);
      });
      this.streamListenerCleanups.push(artifactCleanup);
    }

    // Session created out-of-band by the server (scheduler triggers, IM gateways)
    // — refresh the sessions list so the sidebar shows the new "[定时] xxx"
    // entry without waiting for the first stream message.
    if (cowork.onSessionCreated) {
      const sessionCreatedCleanup = cowork.onSessionCreated(() => {
        void this.loadSessions();
      });
      this.streamListenerCleanups.push(sessionCreatedCleanup);
    }

    // 使用已有平台事件适配层；WebSocket 已按当前账号过滤事件。
    const onEvent = window.electron.ipcRenderer?.on;
    if (onEvent) {
      this.streamListenerCleanups.push(onEvent('cowork.session_updated', () => { void this.loadSessions(); }));
      this.streamListenerCleanups.push(onEvent('cowork.session_deleted', (data: { sessionId?: unknown }) => {
        if (typeof data?.sessionId === 'string') this.removeSession(data.sessionId);
      }));
    }
    const reconnectHandler = () => {
      void this.loadSessions();
      void this.reconcileCurrentSessionAfterReconnect();
    };
    window.addEventListener('boclaw:ws-reconnected', reconnectHandler);
    this.streamListenerCleanups.push(() => {
      window.removeEventListener('boclaw:ws-reconnected', reconnectHandler);
    });

    this.startStreamWatchdog();
    this.streamListenerCleanups.push(() => this.stopStreamWatchdog());
  }

  private markStreamActivity(sessionId: string): void {
    if (store.getState().cowork.currentSessionId !== sessionId) return;
    this.lastStreamActivityAt = Date.now();
  }

  // 每 5s 检查一次：当前会话若处于 running(界面转圈)且已连续 STREAM_SILENCE_MS 无任何
  // stream 事件，主动对账一次。健康流式每 <1s 就有 delta，永远不会触发；只有真正的
  // 推送缺口(后台标签、屏幕共享、微断连)才会命中，把 loadSession 的最新权威快照补回来。
  // 阈值取 20s 以避开合法的长工具执行(如长时间 Bash)造成的正常静默。
  private startStreamWatchdog(): void {
    const STREAM_SILENCE_MS = 20_000;
    const CHECK_INTERVAL_MS = 5_000;
    this.stopStreamWatchdog();
    this.streamWatchdogTimer = window.setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      const state = store.getState().cowork;
      const sid = state.currentSessionId;
      if (!sid) return;
      const running = state.currentSession?.id === sid
        && (state.currentSession?.status === 'running' || state.isStreaming);
      if (!running) return;
      if (this.lastStreamActivityAt === 0) { this.lastStreamActivityAt = Date.now(); return; }
      if (Date.now() - this.lastStreamActivityAt < STREAM_SILENCE_MS) return;
      // 静默过久：补一次对账，并重置计时避免连环触发。
      this.lastStreamActivityAt = Date.now();
      void this.reconcileCurrentSessionAfterReconnect();
    }, CHECK_INTERVAL_MS);
  }

  private stopStreamWatchdog(): void {
    if (this.streamWatchdogTimer !== null) {
      window.clearInterval(this.streamWatchdogTimer);
      this.streamWatchdogTimer = null;
    }
  }

  private async reconcileCurrentSessionAfterReconnect(): Promise<void> {
    const state = store.getState().cowork;
    const currentSessionId = state.currentSessionId;
    if (!currentSessionId) return;

    try {
      await this.loadSession(currentSessionId, { onlyIfCurrent: true });
    } catch (error) {
      console.warn('[Cowork] Failed to reconcile session after WebSocket reconnect:', error);
    }
  }

  private refreshCurrentSessionUsage(sessionId: string): void {
    if (store.getState().cowork.currentSessionId !== sessionId) return;

    const cowork = window.electron?.cowork;
    if (!cowork) return;
    const requestVersion = ++this.nextUsageRefreshRequestVersion;
    this.usageRefreshRequestVersions.set(sessionId, requestVersion);

    void cowork.getSession(sessionId)
      .then((result) => {
        if (!result.success || !result.session?.usage) return;
        if (store.getState().cowork.currentSessionId !== sessionId) return;
        if (this.usageRefreshRequestVersions.get(sessionId) !== requestVersion) return;
        store.dispatch(updateCurrentSessionUsage({
          sessionId,
          usage: result.session.usage,
        }));
      })
      .catch((error) => {
        console.warn('[Cowork] Failed to refresh context usage:', error);
      })
      .finally(() => {
        if (this.usageRefreshRequestVersions.get(sessionId) === requestVersion) {
          this.usageRefreshRequestVersions.delete(sessionId);
        }
      });
  }

  private scheduleCurrentSessionUsageRefresh(sessionId: string): void {
    if (store.getState().cowork.currentSessionId !== sessionId) return;
    if (this.usageRefreshTimers.has(sessionId)) return;

    const timer = window.setTimeout(() => {
      this.usageRefreshTimers.delete(sessionId);
      this.refreshCurrentSessionUsage(sessionId);
    }, 500);
    this.usageRefreshTimers.set(sessionId, timer);
  }

  private cleanupListeners(): void {
    this.streamListenerCleanups.forEach(cleanup => cleanup());
    this.streamListenerCleanups = [];
    this.usageRefreshTimers.forEach(timer => window.clearTimeout(timer));
    this.usageRefreshTimers.clear();
    this.usageRefreshRequestVersions.clear();
  }

  /** 最新列表响应获胜；只合并当前云端会话元数据，不覆盖执行中消息和审批。 */
  async loadSessions(): Promise<void> {
    const epoch = this.initializationEpoch, sequence = ++this.sessionListSequence;
    const result = await window.electron?.cowork?.listSessions();
    if (epoch !== this.initializationEpoch || sequence !== this.sessionListSequence) return;
    if (result?.success && result.sessions) {
      store.dispatch(setCloudSessions(result.sessions));
      const current = store.getState().cowork.currentSession;
      const summary = current?.origin !== 'desktop' ? result.sessions.find(item => item.id === current?.id) : null;
      if (summary) {
        store.dispatch(updateSessionTitle({ sessionId: summary.id, title: summary.title }));
        store.dispatch(updateSessionPinned({ sessionId: summary.id, pinned: !!summary.pinned }));
        if (summary.modelId) store.dispatch(updateSessionModelId({ sessionId: summary.id, modelId: summary.modelId }));
      }
    }
  }

  async loadConfig(): Promise<void> {
    const result = await window.electron?.cowork?.getConfig();
    if (result?.success && result.config) {
      store.dispatch(setConfig(result.config as any));
    }
  }

  async startSession(options: CoworkStartOptions): Promise<CoworkSession | null> {
    const cowork = window.electron?.cowork;
    if (!cowork) {
      console.error('Cowork API not available');
      return null;
    }

    store.dispatch(setStreaming(true));

    const result = await cowork.startSession(options);
    if (result.success && result.session) {
      store.dispatch(addSession(result.session));
      store.dispatch(updateSessionStatus({ sessionId: result.session.id, status: 'running' }));
      return result.session;
    }

    store.dispatch(setStreaming(false));
    console.error('Failed to start session:', result.error);
    return null;
  }

  private async continueSession(
    options: CoworkContinueOptions,
  ): Promise<'started' | 'queued' | 'failed'> {
    const cowork = window.electron?.cowork;
    if (!cowork) {
      console.error('Cowork API not available');
      return 'failed';
    }

    // 服务端会原子判断“立即执行或入队”；请求发出后该会话必然处于运行链中。
    store.dispatch(updateSessionStatus({ sessionId: options.sessionId, status: 'running' }));

    const result = await cowork.continueSession({
      scenario: options.scenario,
      sessionId: options.sessionId,
      prompt: options.prompt,
      systemPrompt: options.systemPrompt,
      activeSkillIds: options.activeSkillIds,
      ...((options as any).sandboxProfileId ? { sandboxProfileId: (options as any).sandboxProfileId } : {}),
      ...((options as any).sandboxImageId ? { sandboxImageId: (options as any).sandboxImageId } : {}),
      ...(options.modelId ? { modelId: options.modelId } : {}),
    } as any);
    if (!result.success) {
      store.dispatch(updateSessionStatus({ sessionId: options.sessionId, status: 'error' }));
      console.error('Failed to continue session:', result.error);
      // 服务端拒绝（最常见是模型没有可用渠道）时必须在对话里留痕。此前只打 console，
      // 请求根本没到执行器、也不会有任何流事件回来，用户看到的就是"发出去了但毫无反应"。
      const failure = result as { error?: string; code?: string; available?: string[]; modelId?: string };
      store.dispatch(addMessage({
        sessionId: options.sessionId,
        message: {
          id: `local-error-${Date.now()}`,
          type: 'system',
          content: failure.error || '发送失败，请重试',
          timestamp: Date.now(),
          metadata: {
            isError: true,
            error: failure.error,
            ...(failure.code === 'MODEL_UNAVAILABLE' ? {
              modelUnavailable: {
                modelId: failure.modelId || options.modelId || '',
                available: failure.available || [],
              },
            } : {}),
          },
        },
      }));
      return 'failed';
    }

    return result.queued ? 'queued' : 'started';
  }

  /**
   * 当前轮仍在执行时只入队；空闲时直接发起。队列按 sessionId 隔离，
   * 所以切换会话不会改变各自的发送顺序。
   */
  async submitOrQueueContinuation(
    options: CoworkContinueOptions,
    _display: { preview: string },
  ): Promise<'queued' | 'started' | 'failed'> {
    return this.continueSession(options);
  }

  /** 编辑失败保持本地草稿，账号切换后的回执不写入新状态。 */
  async editQueuedMessage(sessionId: string, id: string, revision: number, text: string): Promise<void> {
    await this.updateQueuedMessage(() => window.electron.cowork.editQueuedMessage(sessionId, id, revision, text));
  }

  /** 引导送达依赖 SDK 消费回执；HTTP 成功仅说明状态已受理。 */
  async steerQueuedMessage(sessionId: string, id: string, revision: number): Promise<void> {
    await this.updateQueuedMessage(() => window.electron.cowork.steerQueuedMessage(sessionId, id, revision));
  }

  /** 状态统一由队列广播更新，避免迟到 HTTP 快照覆盖较新的广播。 */
  private async updateQueuedMessage(write: () => Promise<{ success: boolean; error?: string }>): Promise<void> {
    const epoch = this.initializationEpoch;
    const result = await write();
    if (epoch !== this.initializationEpoch) throw new Error('QUEUE_IDENTITY_CHANGED');
    if (!result.success) throw new Error(result.error || 'QUEUE_UPDATE_FAILED');
  }

  async cancelQueuedMessage(sessionId: string, id: string): Promise<void> {
    const cowork = window.electron?.cowork;
    if (!cowork?.cancelQueuedMessage) return;

    const result = await cowork.cancelQueuedMessage(sessionId, id);
    if (!result.success) {
      window.dispatchEvent(new CustomEvent('app:showToast', {
        detail: '取消排队消息失败，请重试',
      }));
    }
  }

  async stopSession(sessionId: string): Promise<boolean> {
    const cowork = window.electron?.cowork;
    if (!cowork) return false;

    store.dispatch(clearQueuedMessages(sessionId));

    const result = await cowork.stopSession(sessionId);
    if (result.success) {
      store.dispatch(updateSessionStatus({ sessionId, status: 'idle' }));
      return true;
    }

    console.error('Failed to stop session:', result.error);
    return false;
  }

  /** 只按后端成功回执移除消息；账号切换后的迟到回执不得改写新账号状态。 */
  async deleteTurns(sessionId: string, turnUserMessageIds: string[]): Promise<void> {
    const epoch = this.initializationEpoch;
    const current = store.getState().cowork.currentSession;
    // 回执丢失后的重试可能返回空集合；成功仍确认所选轮次已不存在，移除提交时的旧快照。
    const snapshotIds = current?.id === sessionId ? selectedConversationMessageIds(current.messages, turnUserMessageIds) : [];
    const result = await window.electron.cowork.deleteTurns(sessionId, turnUserMessageIds);
    if (epoch !== this.initializationEpoch) return;
    if (!result.success || !Array.isArray(result.deletedMessageIds)) throw new Error(result.error || 'TURN_DELETE_FAILED');
    store.dispatch(removeSessionMessages({ sessionId, deletedMessageIds: [...new Set([...snapshotIds, ...result.deletedMessageIds])] }));
  }

  /** 同一会话的管理操作互斥；只有当前登录周期的成功回执可以修改本地状态。 */
  private async mutateSession(sessionId: string, write: () => Promise<{ success: boolean }>, commit: () => void): Promise<boolean> {
    if (this.sessionMutations.has(sessionId)) return false;
    const request = Symbol(sessionId), epoch = this.initializationEpoch;
    this.sessionMutations.set(sessionId, request);
    try {
      const result = await write();
      if (epoch !== this.initializationEpoch || !result.success) return false;
      // 早于管理操作的列表快照不能把已删会话或旧标题重新带回页面。
      this.sessionListSequence++;
      commit();
      return true;
    } catch { return false; }
    finally { if (this.sessionMutations.get(sessionId) === request) this.sessionMutations.delete(sessionId); }
  }

  /** 删除确认后再清队列和审批；失败保留用户尚未执行的输入。 */
  async deleteSession(sessionId: string): Promise<boolean> {
    const cowork = window.electron?.cowork;
    if (!cowork) return false;
    return this.mutateSession(sessionId, () => cowork.deleteSession(sessionId), () => this.removeSession(sessionId));
  }

  /** REST 与其它客户端的删除通知共用本地清理，重复通知保持幂等。 */
  private removeSession(sessionId: string): void {
    this.sessionListSequence++;
    store.dispatch(deleteSessionAction(sessionId));
    if (readLastSession() === sessionId) rememberLastSession(null);
  }

  /** 置顶持久化成功后更新列表；重复点击不并发覆盖。 */
  async setSessionPinned(sessionId: string, pinned: boolean): Promise<boolean> {
    const cowork = window.electron?.cowork;
    if (!cowork?.setSessionPinned) return false;
    return this.mutateSession(sessionId, () => cowork.setSessionPinned({ sessionId, pinned }), () => {
      store.dispatch(updateSessionPinned({ sessionId, pinned }));
    });
  }

  /** 名称与服务端使用相同长度/控制字符边界，失败时保留原名。 */
  async renameSession(sessionId: string, title: string): Promise<boolean> {
    const cowork = window.electron?.cowork;
    if (!cowork?.renameSession) return false;
    const normalizedTitle = normalizeSessionTitle(title);
    if (!normalizedTitle) return false;
    return this.mutateSession(sessionId, () => cowork.renameSession({ sessionId, title: normalizedTitle }), () => {
      store.dispatch(updateSessionTitle({ sessionId, title: normalizedTitle }));
    });
  }

  /** 模型保存确认后才更新展示与下一轮输入，不自动续跑或打断当前轮次。 */
  async setSessionModel(sessionId: string, modelId: string): Promise<boolean> {
    const cowork = window.electron?.cowork;
    if (!cowork?.updateSessionModel) return false;
    return this.mutateSession(sessionId, () => cowork.updateSessionModel(sessionId, modelId), () => {
      store.dispatch(updateSessionModelId({ sessionId, modelId }));
    });
  }

  async exportSessionResultImage(options: {
    rect: { x: number; y: number; width: number; height: number };
    defaultFileName?: string;
  }): Promise<{ success: boolean; canceled?: boolean; path?: string; error?: string }> {
    const cowork = window.electron?.cowork;
    if (!cowork?.exportResultImage) {
      return { success: false, error: 'Cowork export API not available' };
    }

    try {
      const result = await cowork.exportResultImage(options);
      return result ?? { success: false, error: 'Failed to export session image' };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to export session image',
      };
    }
  }

  async captureSessionImageChunk(options: {
    rect: { x: number; y: number; width: number; height: number };
  }): Promise<{ success: boolean; width?: number; height?: number; pngBase64?: string; error?: string }> {
    const cowork = window.electron?.cowork;
    if (!cowork?.captureImageChunk) {
      return { success: false, error: 'Cowork capture API not available' };
    }

    try {
      const result = await cowork.captureImageChunk(options);
      return result ?? { success: false, error: 'Failed to capture session image chunk' };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to capture session image chunk',
      };
    }
  }

  async saveSessionResultImage(options: {
    pngBase64: string;
    defaultFileName?: string;
  }): Promise<{ success: boolean; canceled?: boolean; path?: string; error?: string }> {
    const cowork = window.electron?.cowork;
    if (!cowork?.saveResultImage) {
      return { success: false, error: 'Cowork save image API not available' };
    }

    try {
      const result = await cowork.saveResultImage(options);
      return result ?? { success: false, error: 'Failed to save session image' };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to save session image',
      };
    }
  }

  /** 打开（必要时创建）BoMate 主会话并切换到它。 */
  async openSecretarySession(): Promise<CoworkSession | null> {
    try {
      const result = await (window.electron?.cowork as unknown as {
        openSecretarySession?: () => Promise<{ session?: CoworkSession }>;
      })?.openSecretarySession?.();
      const session = result?.session;
      if (!session) return null;
      store.dispatch(setCurrentSessionId(session.id));
      await this.loadSession(session.id);
      return session;
    } catch (error) {
      console.error('Failed to open BoMate session:', error);
      return null;
    }
  }

  async loadSession(
    sessionId: string,
    options: { onlyIfCurrent?: boolean } = {},
  ): Promise<CoworkSession | null> {
    const cowork = window.electron?.cowork;
    if (!cowork) return null;

    // 对齐编程页：选择是同步导航，异步请求只负责给已选择的会话补数据。
    // 这样 A、B 两个详情请求乱序返回时，A 无权把已经切到 B 的视图抢回去。
    if (!options.onlyIfCurrent) {
      store.dispatch(setCurrentSessionId(sessionId));
      rememberLastSession(sessionId);
    }

    const [result, runState, pendingPermissionResult] = await Promise.all([
      cowork.getSession(sessionId),
      cowork.getSessionRunState
        ? cowork.getSessionRunState(sessionId)
        : Promise.resolve({ success: false, running: undefined, queued: undefined, error: 'Run-state API unavailable' }),
      cowork.getPendingPermission
        ? cowork.getPendingPermission(sessionId)
        : Promise.resolve({ success: false, request: undefined, error: 'Pending-permission API unavailable' }),
    ]);
    if (result.success && result.session) {
      if (store.getState().cowork.currentSessionId !== sessionId) {
        return result.session;
      }

      const hydratedSession: CoworkSession = runState.success
        ? {
            ...result.session,
            status: runState.running
              ? 'running'
              : result.session.status === 'running'
                ? 'idle'
                : result.session.status,
          }
        : result.session;

      store.dispatch(setCurrentSession(hydratedSession));
      if (runState.success) {
        store.dispatch(setQueuedMessages({
          sessionId,
          queued: runState.queued ?? [],
        }));
      }

      if (pendingPermissionResult.success) {
        const stale = store.getState().cowork.pendingPermissions.filter(p => p.sessionId === sessionId);
        for (const permission of stale) {
          store.dispatch(dequeuePendingPermission({ requestId: permission.requestId }));
        }
        const request = pendingPermissionResult.request;
        if (request) {
          store.dispatch(enqueuePendingPermission({
            sessionId,
            toolName: request.toolName,
            toolInput: request.toolInput,
            requestId: request.requestId,
            toolUseId: request.toolUseId ?? null,
          }));
        }
      }

      // Hydrate deliverables registered via sandbox IPC so they survive page refresh.
      // The server returns them from in-memory state (if still active) or the
      // ~/.boclaw/session-files/{sessionId}/.deliverables.json cache otherwise.
      if (cowork.getRegisteredDeliverables) {
        cowork.getRegisteredDeliverables(sessionId)
          .then((r) => {
            if (r.success && r.paths?.length) {
              store.dispatch(addRegisteredDeliverables({ sessionId, paths: r.paths }));
            }
          })
          .catch(() => { /* non-fatal */ });
      }

      return hydratedSession;
    }

    console.error('Failed to load session:', result.error);
    return null;
  }

  async respondToPermission(requestId: string, result: CoworkPermissionResult): Promise<boolean> {
    const cowork = window.electron?.cowork;
    if (!cowork) return false;

    const response = await cowork.respondToPermission({ requestId, result });
    if (response.success) {
      store.dispatch(dequeuePendingPermission({ requestId }));
      return true;
    }
    if (response.expired) {
      // WEB-01 / OPS-06 #6: the request can no longer be answered — close it and say so.
      store.dispatch(dequeuePendingPermission({ requestId }));
      window.dispatchEvent(new CustomEvent('app:showToast', { detail: response.error || '该确认请求已失效' }));
      return false;
    }

    console.error('Failed to respond to permission:', response.error);
    return false;
  }

  async updateConfig(config: CoworkConfigUpdate): Promise<boolean> {
    const cowork = window.electron?.cowork;
    if (!cowork) return false;

    const result = await cowork.setConfig(config);
    if (result.success) {
      const currentConfig = store.getState().cowork.config;
      store.dispatch(setConfig({ ...currentConfig, ...config }));
      return true;
    }

    console.error('Failed to update config:', result.error);
    return false;
  }

  async getApiConfig(): Promise<CoworkApiConfig | null> {
    if (!window.electron?.getApiConfig) {
      return null;
    }
    return window.electron.getApiConfig();
  }

  async checkApiConfig(): Promise<{ hasConfig: boolean; config: CoworkApiConfig | null; error?: string } | null> {
    if (!window.electron?.checkApiConfig) {
      return null;
    }
    return window.electron.checkApiConfig();
  }

  async saveApiConfig(config: CoworkApiConfig): Promise<{ success: boolean; error?: string } | null> {
    if (!window.electron?.saveApiConfig) {
      return null;
    }
    return window.electron.saveApiConfig(config);
  }


  async listMemoryEntries(input: {
    query?: string;
    status?: 'created' | 'stale' | 'deleted' | 'all';
    includeDeleted?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<CoworkUserMemoryEntry[]> {
    const api = window.electron?.cowork?.listMemoryEntries;
    if (!api) throw new Error(i18nService.t('memoryFile_unavailable'));
    const result = await api(input);
    if (!result?.success || !Array.isArray(result.entries)) throw new Error(result?.error || i18nService.t('memoryFile_unavailable'));
    return result.entries;
  }

  async createMemoryEntry(input: {
    text: string;
    confidence?: number;
    isExplicit?: boolean;
  }): Promise<CoworkUserMemoryEntry | null> {
    const api = window.electron?.cowork?.createMemoryEntry;
    if (!api) throw new Error(i18nService.t('memoryFile_unavailable'));
    const result = await api(input);
    if (!result?.success || !result.entry) throw new Error(result?.error || i18nService.t('coworkMemoryCrudSaveFailed'));
    return result.entry;
  }

  async updateMemoryEntry(input: {
    id: string;
    text?: string;
    confidence?: number;
    status?: 'created' | 'stale' | 'deleted';
    isExplicit?: boolean;
  }): Promise<CoworkUserMemoryEntry | null> {
    const api = window.electron?.cowork?.updateMemoryEntry;
    if (!api) throw new Error(i18nService.t('memoryFile_unavailable'));
    const result = await api(input);
    if (!result?.success || !result.entry) throw new Error(result?.error || i18nService.t('coworkMemoryCrudSaveFailed'));
    return result.entry;
  }

  async deleteMemoryEntry(input: { id: string }): Promise<boolean> {
    const api = window.electron?.cowork?.deleteMemoryEntry;
    if (!api) throw new Error(i18nService.t('memoryFile_unavailable'));
    const result = await api(input);
    if (!result?.success) throw new Error(result?.error || i18nService.t('coworkMemoryCrudDeleteFailed'));
    return true;
  }

  async getMemoryStats(): Promise<CoworkMemoryStats | null> {
    const api = window.electron?.cowork?.getMemoryStats;
    if (!api) throw new Error(i18nService.t('memoryFile_unavailable'));
    const result = await api();
    if (!result?.success || !result.stats) throw new Error(result?.error || i18nService.t('memoryFile_unavailable'));
    return result.stats;
  }

  async generateSessionTitle(prompt: string | null): Promise<string | null> {
    if (!window.electron?.generateSessionTitle) {
      return null;
    }
    return window.electron.generateSessionTitle(prompt);
  }

  async getRecentCwds(limit?: number): Promise<string[]> {
    if (!window.electron?.getRecentCwds) {
      return [];
    }
    return window.electron.getRecentCwds(limit);
  }

  // ── Sandbox Profiles ─────────────────────────────────────────────────────

  private _authHeaders(): Record<string, string> {
    const token = localStorage.getItem('boclaw_token');
    const userId = localStorage.getItem('boclaw_user_id');
    const role = localStorage.getItem('boclaw_user_role');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (userId) headers['X-Boclaw-User-Id'] = userId;
    if (role) headers['X-Boclaw-User-Role'] = role;
    return headers;
  }

  async getSandboxProfiles(): Promise<SandboxProfile[]> {
    try {
      const resp = await fetch('/api/cowork/sandbox-profiles', { headers: this._authHeaders() });
      if (!resp.ok) return [];
      return await resp.json();
    } catch {
      return [];
    }
  }

  // ── Sandbox Image Catalog (read-only, fetched from platform API) ─────────

  async getSandboxImageCatalog(): Promise<SandboxImage[]> {
    try {
      const resp = await fetch('/api/cowork/sandbox-images', { headers: this._authHeaders() });
      if (!resp.ok) return [];
      return await resp.json();
    } catch {
      return [];
    }
  }

  clearSession(): void {
    store.dispatch(clearCurrentSession());
    // 用户主动回到"新建任务"首页，刷新后就应该还停在首页。
    rememberLastSession(null);
  }

  /** 无凭证副作用的 VNC readiness 探活；不会签发 ticket 或写 cookie。 */
  async getSessionVncState(sessionId: string): Promise<{
    ready: boolean;
    vncUrl: string | null;
    instance: string | null;
  } | null> {
    try {
      const resp = await fetch(`/api/cowork/sessions/${sessionId}/vnc-state`, {
        headers: this._authHeaders(),
        cache: 'no-store',
      });
      if (!resp.ok) return null;
      const data = await resp.json();
      if (typeof data.ready !== 'boolean') return null;
      if (!data.ready) return { ready: false, vncUrl: null, instance: null };
      if (typeof data.vncUrl !== 'string' || !data.vncUrl) return null;
      if (typeof data.instance !== 'string' || !data.instance.startsWith('vni_')) return null;
      return { ready: true, vncUrl: data.vncUrl, instance: data.instance };
    } catch {
      return null;
    }
  }

  /** 获取同源 VNC 代理入口、一次性 bootstrap ticket 与不透明 sandbox 实例指纹。 */
  async getSessionVncUrl(sessionId: string): Promise<{
    vncUrl: string;
    t: string;
    instance: string;
  } | null> {
    try {
      const resp = await fetch(`/api/cowork/sessions/${sessionId}/vnc-url`, {
        headers: this._authHeaders(),
      });
      if (!resp.ok) return null;
      const data = await resp.json();
      if (typeof data.vncUrl !== 'string' || !data.vncUrl) return null;
      if (typeof data.t !== 'string' || !data.t.startsWith('sat_')) return null;
      if (typeof data.instance !== 'string' || !data.instance.startsWith('vni_')) return null;
      return { vncUrl: data.vncUrl, t: data.t, instance: data.instance };
    } catch {
      return null;
    }
  }

  destroy(): void {
    this.initializationEpoch += 1;
    this.sessionMutations.clear();
    this.sessionListSequence++;
    this.sessionWatchCleanup?.();
    this.sessionWatchCleanup = null;
    this.navigationCleanup?.();
    this.navigationCleanup = null;
    this.cleanupListeners();
    this.initialized = false;
  }
}

export const coworkService = new CoworkService();
window.addEventListener('auth:identityChanging', () => coworkService.destroy());
